import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API, fmt$, fmtN, fmtPct } from "../lib/appCommon";
import OCIControlCenter from "./OCIControlCenter";
import OmegaBrainCard from "./OmegaBrainCard";
import OrderQueueCard from "./OrderQueueCard";
import QuickTradeModal from "./QuickTradeModal";
import TrailStopModal from "./TrailStopModal";
import { OmegaWatchRoom } from "./Positions";
import "./RiskTab.css";

// ─── Icons (same stroke convention as the rest of the app's nav icons) ────────
const ICON_PATHS = {
  cpu: <><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3"/></>,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></>,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
  shield: <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/>,
  chart: <><path d="M3 3v18h18"/><path d="M18.7 8 13 13.7l-4-4L3 16"/></>,
  alert: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
};
function Icon({ name }) {
  const p = ICON_PATHS[name];
  if (!p) return null;
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
}

const SECTION_ACCENT = {
  control:   { accent: "var(--blue)",   bg: "rgba(96,165,250,0.13)" },
  watchroom: { accent: "var(--purple)", bg: "rgba(167,139,250,0.13)" },
  portfolio: { accent: "var(--green)",  bg: "rgba(74,222,128,0.13)" },
  riskgate:  { accent: "#f87171",       bg: "rgba(248,113,113,0.13)" },
  market:    { accent: "var(--rd-gold)", bg: "var(--rd-gold-bg)" },
};

// ─── Ring gauge — Portfolio Heat ───────────────────────────────────────────────
function RingGauge({ pct, size = 84, stroke = 9, color }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - clamped / 100);
  return (
    <div className="rdash-gauge" style={{ width: size, height: size, position: "relative" }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--layer2)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.22, fontWeight: 800, color }}>
        {clamped.toFixed(0)}%
      </div>
    </div>
  );
}

// ─── Section shell — one section mounted/visible at a time ────────────────────
function Section({ id, activeId, title, icon, sub, children }) {
  if (id !== activeId) return null;
  return (
    <div className="rdash-section" style={{ "--accent": SECTION_ACCENT[id].accent, "--accent-bg": SECTION_ACCENT[id].bg }} data-testid={`rdash-section-${id}`}>
      <div className="rdash-section-head">
        <div>
          <div className="rdash-section-title"><Icon name={icon} />{title}</div>
          {sub && <div className="rdash-section-sub">{sub}</div>}
        </div>
      </div>
      <div className="rdash-section-body">{children}</div>
    </div>
  );
}

// ─── Portfolio (merged: snapshot stats + Greeks + activity tape) ──────────────
function PortfolioSection({ riskData, portfolioGreeks, activityTape, equitySparkline }) {
  const heat = riskData?.portfolioHeat || 0;
  const heatColor = heat > 65 ? "#f87171" : heat > 35 ? "#fbbf24" : "#4ade80";

  const sparkPts = (equitySparkline || []).map(p => p.e).filter(v => v != null);
  const eqUp = sparkPts.length > 1 ? sparkPts[sparkPts.length - 1] >= sparkPts[0] : true;
  const eqColor = eqUp ? "#4ade80" : "#f87171";
  const eqChart = sparkPts.length > 1 ? {
    labels: sparkPts.map(() => ""),
    datasets: [{ data: sparkPts, borderColor: eqColor, backgroundColor: eqColor + "22", fill: true, pointRadius: 0, borderWidth: 1.8, tension: 0.3 }],
  } : null;
  const eqOpts = { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } };

  const g = portfolioGreeks || {};
  const hasGreeks = (portfolioGreeks?.optionPositions || 0) > 0;
  const greekRows = [
    { name: "Delta", val: g.delta || 0, color: "#60a5fa", max: 100 },
    { name: "Gamma", val: g.gamma || 0, color: "#a78bfa", max: 5 },
    { name: "Theta", val: g.theta || 0, color: "#f87171", max: 20 },
    { name: "Vega", val: g.vega || 0, color: "#38bdf8", max: 20 },
  ];

  return (
    <div className="rdash-layout">
      <div>
        <div className="rdash-stat-grid" style={{ marginBottom: 14 }}>
          <div className="rdash-stat-chip"><div className="l">Equity</div><div className="v" style={{ color: "var(--green)" }}>{fmt$(riskData?.equity)}</div></div>
          <div className="rdash-stat-chip"><div className="l">Buying Power</div><div className="v">{fmt$(riskData?.buyingPower)}</div></div>
          <div className="rdash-stat-chip"><div className="l">Total at Risk</div><div className="v" style={{ color: "var(--red)" }}>{fmt$(riskData?.totalAtRisk)}</div></div>
          <div className="rdash-stat-chip"><div className="l">Open Positions</div><div className="v">{riskData?.openPositions || 0}</div></div>
        </div>

        <div className="rdash-side-title" style={{ marginBottom: 8 }}>Equity Curve</div>
        <div className="rdash-chart-box" style={{ marginBottom: 16 }}>
          {eqChart ? <Line data={eqChart} options={eqOpts} /> : <div className="rdash-chart-empty">Not enough history yet — check back after a few snapshots.</div>}
        </div>

        <div className="rdash-side-title" style={{ marginBottom: 8 }}>Portfolio Greeks</div>
        {hasGreeks ? (
          <div style={{ marginBottom: 16 }}>
            {greekRows.map(r => (
              <div key={r.name} className="rdash-bar-row">
                <span className="name">{r.name}</span>
                <div className="rdash-bar-track"><i style={{ width: `${Math.min(100, Math.abs(r.val) / r.max * 100)}%`, background: r.color }} /></div>
                <span className="val" style={{ color: r.color }}>{r.val >= 0 ? "+" : ""}{r.val.toFixed(r.name === "Delta" ? 1 : 3)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rdash-card" style={{ padding: 16, textAlign: "center", color: "var(--dim)", fontSize: 12, marginBottom: 16 }}>No open option positions — Greeks appear once you have active contracts.</div>
        )}

        <div className="rdash-side-title" style={{ marginBottom: 8 }}>Activity Tape</div>
        {activityTape.length > 0 ? (
          <div>
            {activityTape.map((a, i) => (
              <div key={i} className="rdash-card" data-testid={`tape-${i}`} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 11,
                borderLeft: `3px solid ${a.won ? "#4ade80" : "#f87171"}`,
              }}>
                <span style={{ fontWeight: 700, color: a.won ? "#4ade80" : "#f87171", width: 16 }}>{a.won ? "W" : "L"}</span>
                <span style={{ fontWeight: 600, color: "var(--text)", minWidth: 50 }}>{a.ticker}</span>
                <span style={{ color: "var(--muted)" }}>Zi {a.ziScore}</span>
                <span style={{ color: a.pnl >= 0 ? "#4ade80" : "#f87171", fontWeight: 600 }}>{a.pnl >= 0 ? "+" : ""}{typeof a.pnl === "number" ? `$${a.pnl.toFixed(2)}` : "$0.00"}</span>
                <span style={{ color: "var(--dim)", flex: 1, textAlign: "right", fontSize: 9 }}>{a.reason}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rdash-card" style={{ padding: 24, textAlign: "center", color: "var(--dim)", fontSize: 12 }}>No activity tape yet — trades will appear here as they close.</div>
        )}
      </div>

      <div className="rdash-side">
        <div className="rdash-side-card">
          <div className="rdash-side-title">Portfolio Heat</div>
          <div className="rdash-gauge-wrap">
            <RingGauge pct={heat} color={heatColor} />
            <div>
              <div className="rdash-gauge-sub">{heat > 65 ? "Elevated — sizing is stretched" : heat > 35 ? "Moderate exposure" : "Comfortably within limits"}</div>
            </div>
          </div>
        </div>
        <div className="rdash-side-card">
          <div className="rdash-side-title">P&L</div>
          <div className="rdash-side-stat-row"><span className="l">Weekly</span><span className="v" style={{ color: (riskData?.weeklyPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(riskData?.weeklyPnl)}</span></div>
          <div className="rdash-side-stat-row"><span className="l">Monthly</span><span className="v" style={{ color: (riskData?.monthlyPnl || 0) >= 0 ? "var(--green)" : "var(--red)" }}>{fmt$(riskData?.monthlyPnl)}</span></div>
        </div>
        {hasGreeks && (
          <div className="rdash-insight">
            <span className="tag">Read</span>
            {g.delta >= 0 ? "Net long-biased" : "Net short-biased"} portfolio. Theta is {g.theta < 0 ? "negative — time decay works against you" : "positive — time decay works in your favor"}. Vega {Math.abs(g.vega || 0).toFixed(2)} means a 1pt IV move shifts P&L by ~${Math.abs(g.vega || 0).toFixed(0)}.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Risk Gate (merged: Kill Switches + Capital Tiers) ─────────────────────────
const TIER_COLORS = { survival: "#fbbf24", builder: "#4ade80", operator: "#38bdf8", sovereign: "#a78bfa" };

function RiskGateSection({ killSwitches, omegaInfo }) {
  const switches = killSwitches?.switches || [];
  const tiers = (omegaInfo?.allTiers || []).map(t => ({
    id: t.id, label: t.label, min: t.min, max: t.max >= 99999999 ? null : t.max,
    color: TIER_COLORS[t.id] || "#fbbf24",
    risk: `${t.max_risk_pct}%`, slots: t.max_positions, kelly: `${t.kelly_frac.toFixed(2)}×`,
    daily: `${t.daily_loss_cap}%`, scanner: t.scannerPreview ? `${t.scannerPreview}…` : "—",
  }));
  const currentId = omegaInfo?.capitalTier?.id || "survival";
  const equity = omegaInfo?.equity || 0;
  const nextTier = omegaInfo?.nextTier;
  const currentIdx = tiers.findIndex(t => t.id === currentId);
  const progressPct = nextTier ? Math.min(100, (equity / nextTier.minEquity) * 100) : 100;

  return (
    <div className="rdash-layout">
      <div>
        {killSwitches ? (
          <>
            {killSwitches.anyFired && (
              <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 10, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", fontSize: 12, color: "#f87171", fontWeight: 600 }}>
                ⚠ One or more kill switches are active on the real-money account — new REAL entries are halted until conditions clear. zBucks paper trading is unaffected.
              </div>
            )}
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--dim, #8b98b0)", marginBottom: 6 }}>
              Real-Money Account
            </div>
            <div className="rdash-ks-strip">
              {switches.map((ks, i) => {
                const c = ks.fired ? "#f87171" : "#4ade80";
                return (
                  <div key={i} className="rdash-ks-chip" title={ks.message} data-testid={`ks-${ks.switch}`} style={{ borderColor: ks.fired ? "rgba(248,113,113,0.3)" : "rgba(74,222,128,0.18)", background: ks.fired ? "rgba(248,113,113,0.06)" : "rgba(74,222,128,0.04)" }}>
                    <span className="dot" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />
                    <div className="name" style={{ color: c }}>{ks.name}</div>
                    <div className="state" style={{ color: c }}>{ks.fired ? "FIRED" : "CLEAR"}</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : <div className="rdash-card" style={{ padding: 24, textAlign: "center", color: "var(--dim)" }}>Loading kill switch status…</div>}

        <div className="rdash-side-title" style={{ margin: "16px 0 8px" }}>Capital Tier Ladder</div>
        {nextTier ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: "var(--text)" }}>Progress to {nextTier.label}</span>
              <span style={{ color: "var(--muted)" }}>{nextTier.needed === 0 ? "Ready!" : `$${Number(nextTier.needed).toLocaleString()} away`}</span>
            </div>
            <div className="rdash-tier-progress-track">
              <i style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${tiers[currentIdx]?.color}88, ${tiers[Math.min(currentIdx + 1, tiers.length - 1)]?.color})` }} />
            </div>
          </div>
        ) : tiers.length > 0 && (
          <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 12, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.2)", fontSize: 12, color: "#a78bfa", fontWeight: 700, textAlign: "center" }}>
            ✦ Sovereign Tier — Maximum Level Unlocked
          </div>
        )}
        {tiers.map((t, i) => {
          const isActive = t.id === currentId;
          const isPast = i < currentIdx;
          return (
            <div key={t.id} className={`rdash-tier-row ${isActive ? "active" : ""}`} style={{ opacity: isPast ? 0.5 : isActive ? 1 : 0.55, borderColor: isActive ? `${t.color}50` : "var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 9, height: 9, borderRadius: "50%", background: t.color, flexShrink: 0, boxShadow: isActive ? `0 0 8px ${t.color}` : "none" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isActive ? t.color : "var(--text)" }}>{t.label}</span>
                    <span style={{ fontSize: 9, color: "var(--dim)" }}>{t.min === 0 ? "$0" : `$${(t.min / 1000).toFixed(0)}K`}{t.max ? `–$${(t.max / 1000).toFixed(0)}K` : "+"}</span>
                    {isActive && <span className="rdash-tier-pill" style={{ background: `${t.color}20`, color: t.color, marginLeft: "auto" }}>ACTIVE</span>}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 5 }}>
                    {[`Risk ${t.risk}`, `${t.slots} slots`, `Kelly ${t.kelly}`, `Cap ${t.daily}`].map((label, pi) => (
                      <span key={pi} style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: isActive ? `${t.color}15` : "var(--layer2)", color: isActive ? t.color : "var(--muted)" }}>{label}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--dim)" }}>Scans: {t.scanner}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rdash-side">
        <div className="rdash-side-card">
          <div className="rdash-side-title">Active Locks</div>
          <div className="rdash-side-stat-row"><span className="l">Max trade risk (CVaR)</span><span className="v" style={{ color: "var(--green)" }}>{omegaInfo?.capitalTier?.max_risk_pct || 3}%</span></div>
          <div className="rdash-side-stat-row"><span className="l">Max positions</span><span className="v">{omegaInfo?.capitalTier?.max_positions || 3}</span></div>
          <div className="rdash-side-stat-row"><span className="l">Daily loss cap</span><span className="v" style={{ color: "var(--red)" }}>{omegaInfo?.capitalTier?.daily_loss_cap || 5}%</span></div>
          <div className="rdash-side-stat-row"><span className="l">Drawdown cap</span><span className="v" style={{ color: "var(--red)" }}>{omegaInfo?.capitalTier?.drawdown_cap || 15}%</span></div>
          <div className="rdash-side-stat-row"><span className="l">Kelly fraction</span><span className="v" style={{ color: "#38bdf8" }}>{omegaInfo?.capitalTier?.kelly_frac || 0.20}</span></div>
        </div>
        <div className="rdash-insight">
          <span className="tag">Ω Risk Gate 6.0</span>
          Running {(omegaInfo?.autonomyLevel || "assisted").toUpperCase()} autonomy on the {omegaInfo?.capitalTier?.label || "—"} tier. {killSwitches?.anyFired ? "New entries are currently halted." : "All switches clear — normal operation."}
        </div>
      </div>
    </div>
  );
}

// ─── Market Lens (upgraded Charts) ─────────────────────────────────────────────
const TF_DAYS = { "1W": 7, "1M": 30, "3M": 90, "1Y": 365 };
const QUICK_SYMBOLS = ["SPY", "QQQ", "AAPL", "NVDA", "TSLA", "BTC"];

function MarketLensSection() {
  const [symbol, setSymbol] = useState("SPY");
  const [inputVal, setInputVal] = useState("SPY");
  const [tf, setTf] = useState("3M");
  const [indicators, setIndicators] = useState(null);
  const [bars, setBars] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      axios.get(`${API}/zi/regime?symbol=${symbol}`),
      axios.get(`${API}/market/ohlcv?symbol=${symbol}&days=365`),
    ]).then(([regRes, ohlcvRes]) => {
      if (cancelled) return;
      if (regRes.status === "fulfilled") setIndicators(regRes.value.data);
      setBars(ohlcvRes.status === "fulfilled" ? (ohlcvRes.value.data?.bars || []) : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [symbol]);

  const sliced = useMemo(() => {
    const days = TF_DAYS[tf];
    const all = bars || [];
    return all.length > days ? all.slice(-days) : all;
  }, [bars, tf]);

  const closes = sliced.map(b => b.c).filter(v => v != null);
  const up = closes.length > 1 ? closes[closes.length - 1] >= closes[0] : true;
  const color = up ? "#4ade80" : "#f87171";
  const priceChart = closes.length > 1 ? {
    labels: closes.map(() => ""),
    datasets: [{ data: closes, borderColor: color, backgroundColor: color + "1f", fill: true, pointRadius: 0, borderWidth: 1.8, tension: 0.3 }],
  } : null;
  const priceOpts = { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } };

  const volBars = sliced.map(b => b.v || 0);
  const volMax = Math.max(...volBars, 1);

  const rsiVals = closes.map((_, i) => {
    if (i < 14) return null;
    let g = 0, l = 0;
    for (let j = i - 13; j <= i; j++) { const d = closes[j] - closes[j - 1]; if (d > 0) g += d; else l -= d; }
    const ag = g / 14, al = l / 14;
    return al === 0 ? 100 : 100 - 100 / (1 + ag / al);
  });
  const rsiChart = { labels: closes.map(() => ""), datasets: [{ data: rsiVals, borderColor: "#facc15", backgroundColor: "rgba(250,204,21,0.06)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 }] };
  const rsiOpts = { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { min: 0, max: 100, ticks: { color: "var(--dim)", font: { size: 9 }, stepSize: 50 }, grid: { color: "var(--grid)" } } } };

  const submit = () => { const v = inputVal.trim().toUpperCase(); if (v) setSymbol(v); };
  const lastClose = closes[closes.length - 1];
  const changePct = closes.length > 1 ? ((lastClose - closes[closes.length - 2]) / closes[closes.length - 2]) * 100 : null;

  return (
    <div className="rdash-layout">
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 800 }}>{symbol}</span>
            {lastClose != null && <span style={{ fontSize: 15, fontWeight: 700 }}>${fmtN(lastClose, 2)}</span>}
            {changePct != null && <span style={{ fontSize: 12, fontWeight: 700, color }}>{changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%</span>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input className="inp" value={inputVal} onChange={e => setInputVal(e.target.value.toUpperCase())} onKeyDown={e => e.key === "Enter" && submit()} style={{ width: 90 }} data-testid="price-symbol-input" />
            <button className="rdash-btn primary" onClick={submit} data-testid="load-chart-btn">Load</button>
          </div>
        </div>

        <div className="rdash-tf-row" style={{ marginBottom: 10 }}>
          {Object.keys(TF_DAYS).map(k => <button key={k} className={tf === k ? "on" : ""} onClick={() => setTf(k)}>{k}</button>)}
        </div>

        {indicators && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: "var(--layer1)", fontSize: 11, alignItems: "center", border: "1px solid var(--border)" }}>
            <span>Regime: <span className={`tag ${String(indicators.regime).includes("bull") ? "t-bull" : String(indicators.regime).includes("bear") ? "t-bear" : "t-neu"}`}>{(indicators.regimeLabel || indicators.regime || "").toUpperCase()}</span></span>
            <span style={{ color: "var(--border)" }}>|</span>
            <span style={{ color: "var(--muted)" }}>RSI <strong style={{ color: "var(--text)" }}>{fmtN(indicators.indicators?.rsi, 1)}</strong></span>
            <span style={{ color: "var(--muted)" }}>Vol <strong style={{ color: "var(--text)" }}>{fmtN(indicators.currentVol, 1)}%</strong></span>
            <span style={{ color: "var(--muted)" }}>Confidence <strong style={{ color: "var(--text)" }}>{fmtN((indicators.confidence || 0) * 100, 0)}%</strong></span>
          </div>
        )}

        <div className="rdash-chart-box">
          {loading ? <div className="rdash-chart-empty"><span className="spin" style={{ marginRight: 8 }} />Loading…</div>
            : priceChart ? <Line data={priceChart} options={priceOpts} /> : <div className="rdash-chart-empty">No chart data</div>}
        </div>
        {!loading && volBars.some(v => v > 0) && (
          <div className="rdash-vol-box">
            <svg viewBox="0 0 300 30" preserveAspectRatio="none" style={{ width: "100%", height: "100%", display: "block" }}>
              {volBars.map((v, i) => {
                const bw = Math.max(1, 300 / volBars.length - 1);
                const h = Math.max(1, (v / volMax) * 28);
                return <rect key={i} x={i * (300 / volBars.length)} y={30 - h} width={bw} height={h} fill={color} opacity="0.5" />;
              })}
            </svg>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--dim)", margin: "6px 0 16px" }}>
          <span>{tf} ago</span><span>Now</span>
        </div>

        <div className="rdash-side-title" style={{ marginBottom: 8 }}>RSI (14)</div>
        <div className="rdash-chart-box short">
          {rsiVals.some(v => v != null) ? <Line data={rsiChart} options={rsiOpts} /> : <div className="rdash-chart-empty">No RSI data</div>}
        </div>
      </div>

      <div className="rdash-side">
        <div className="rdash-side-card">
          <div className="rdash-side-title">Quick Symbols</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {QUICK_SYMBOLS.map(s => (
              <button key={s} className="rdash-btn ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={() => { setInputVal(s); setSymbol(s); }}>{s}</button>
            ))}
          </div>
        </div>
        {indicators && (
          <div className="rdash-insight">
            <span className="tag">Ω Read</span>
            {symbol} is in a {(indicators.regimeLabel || indicators.regime || "neutral").toLowerCase()} regime with {fmtN(indicators.currentVol, 1)}% annualized vol. RSI at {fmtN(indicators.indicators?.rsi, 0)} reads {(indicators.indicators?.rsi || 50) > 70 ? "overbought" : (indicators.indicators?.rsi || 50) < 30 ? "oversold" : "neutral"}.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Risk Dashboard Tab (root) ─────────────────────────────────────────────────
function RiskTab() {
  const [activeId, setActiveId] = useState("control");
  const [riskData, setRiskData] = useState(null);
  const [killSwitches, setKillSwitches] = useState(null);
  const [activityTape, setActivityTape] = useState([]);
  const [portfolioGreeks, setPortfolioGreeks] = useState(null);
  const [equitySparkline, setEquitySparkline] = useState([]);
  const [omegaInfo, setOmegaInfo] = useState(null);
  const [quickTrade, setQuickTrade] = useState(null);
  const [trailStop, setTrailStop] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/risk/dashboard`),
      axios.get(`${API}/omega/kill-switches`),
      axios.get(`${API}/omega/status`),
      axios.get(`${API}/preload`),
    ]).then(([riskRes, ksRes, omegaRes, preloadRes]) => {
      if (riskRes.status === "fulfilled") {
        setRiskData(riskRes.value.data);
        if (riskRes.value.data?.portfolioGreeks) setPortfolioGreeks(riskRes.value.data.portfolioGreeks);
      }
      if (ksRes.status === "fulfilled") setKillSwitches(ksRes.value.data);
      if (omegaRes.status === "fulfilled") setOmegaInfo(omegaRes.value.data);
      if (preloadRes.status === "fulfilled") {
        setActivityTape(preloadRes.value.data?.activityTape || []);
        setEquitySparkline(preloadRes.value.data?.equitySparkline || []);
      }
    });
  }, []);

  const SECTIONS = [
    { id: "control",   label: "Control",     icon: "cpu" },
    { id: "watchroom", label: "Watch Room",  icon: "eye" },
    { id: "portfolio", label: "Portfolio",   icon: "briefcase" },
    { id: "riskgate",  label: "Risk Gate",   icon: "shield", alert: killSwitches?.anyFired },
    { id: "market",    label: "Market Lens", icon: "chart" },
  ];

  const rootAccent = SECTION_ACCENT[activeId] || SECTION_ACCENT.control;

  return (
    <div className="rdash-root" style={{ "--accent": rootAccent.accent, "--accent-bg": rootAccent.bg }} data-testid="risk-tab">
      <div className="rdash-banner">
        <div>
          <div className="rdash-banner-title">
            <Icon name="alert" />
            <span>Risk Dashboard</span>
            <span className="tag t-bull" style={{ fontSize: 8 }}>Live</span>
          </div>
          <div className="rdash-banner-sub">
            {omegaInfo ? `${omegaInfo.capitalTier?.label || "—"} Tier · ${(omegaInfo.autonomyLevel || "assisted").toUpperCase()} Mode` : "Loading risk state..."}
          </div>
        </div>
        <div className="rdash-banner-stats">
          <div className="rdash-banner-stat"><div className="v" style={{ color: (riskData?.portfolioHeat || 0) > 50 ? "var(--red)" : "var(--green)" }}>{fmtPct(riskData?.portfolioHeat)}</div><div className="l">Heat</div></div>
          <div className="rdash-banner-stat"><div className="v">{riskData?.openPositions || 0}</div><div className="l">Positions</div></div>
          <div className="rdash-banner-stat"><div className="v" style={{ color: "var(--green)" }}>{fmt$(riskData?.equity)}</div><div className="l">Equity</div></div>
        </div>
      </div>

      <div className="rdash-jump-strip">
        {SECTIONS.map(s => (
          <button key={s.id} className={`${activeId === s.id ? "on" : ""} ${s.alert ? "alert" : ""}`} onClick={() => setActiveId(s.id)} data-testid={`jump-${s.id}`}>
            <Icon name={s.icon} />{s.label}
            {s.alert && <span className="badge">!</span>}
          </button>
        ))}
      </div>

      <Section id="control" activeId={activeId} title="Control" icon="cpu" sub="Omega Brain · OCI Control Center · Order Queue">
        <OmegaBrainCard />
        <div style={{ marginTop: 14 }}><OCIControlCenter /></div>
        <div style={{ marginTop: 14 }}><OrderQueueCard /></div>
      </Section>

      <Section id="watchroom" activeId={activeId} title="Watch Room" icon="eye" sub="Live monitoring across every open position">
        <OmegaWatchRoom onQuickTrade={(position, mode) => setQuickTrade({ position, mode })} onTrailStop={(position) => setTrailStop({ position })} />
      </Section>

      <Section id="portfolio" activeId={activeId} title="Portfolio" icon="briefcase" sub="Snapshot, equity curve, Greeks, and closed-trade tape">
        <PortfolioSection riskData={riskData} portfolioGreeks={portfolioGreeks} activityTape={activityTape} equitySparkline={equitySparkline} />
      </Section>

      <Section id="riskgate" activeId={activeId} title="Risk Gate" icon="shield" sub="Kill switches + the capital tier ladder that sets your limits">
        <RiskGateSection killSwitches={killSwitches} omegaInfo={omegaInfo} />
      </Section>

      <Section id="market" activeId={activeId} title="Market Lens" icon="chart" sub="Any symbol — regime, RSI, volume">
        <MarketLensSection />
      </Section>

      {quickTrade && <QuickTradeModal position={quickTrade.position} mode={quickTrade.mode} onClose={() => setQuickTrade(null)} onSuccess={() => setQuickTrade(null)} />}
      {trailStop && <TrailStopModal position={trailStop.position} onClose={() => setTrailStop(null)} onSuccess={() => setTrailStop(null)} />}
    </div>
  );
}

export default RiskTab;
